import {
    Box,
    Heading,
    Stack,
    ThemeFontSizeVariable,
    ThemeSpaceVariable,
} from "@caspeco/casper-ui-library";
import { useTranslation } from "react-i18next";
import SectionOfAvailableTimes from "components/selection-components/select-time/section-of-available-times";
import { TimeSetSkeleton } from "components/selection-components/select-time/time-set/time-set-skeleton";
import { useStepTracking } from "logger/use-step-tracking";
import { TrackingStepName } from "logger/user-tracking";
import { TimeSlot } from "types/time-slot";
import { AvailableTimesSection } from "types/available-times";
import { usePageTracking } from "logger/use-page-tracking";
import { PageType } from "logger/page-tracking";
import { useMemo } from "react";

interface EditTimeViewProps {
    availableTimesSections: AvailableTimesSection[];
    selectedTimeSlot: TimeSlot;
    setTime: (time: TimeSlot) => void;
    isLoading?: boolean;
}

const EditTimeView = ({
    availableTimesSections,
    selectedTimeSlot,
    setTime,
    isLoading = false,
}: EditTimeViewProps) => {
    const { t } = useTranslation();

    useStepTracking({
        stepName: TrackingStepName.EditBookingTime,
    });

    const timeSlotCount = useMemo(() => {
        return availableTimesSections.reduce((mainCounter, section) => {
            const interactiveCount = section.timeSets.reduce((acc: number, timeSet) => {
                return acc + (timeSet.times?.length || 0);
            }, 0);
            return mainCounter + interactiveCount;
        }, 0);
    }, [availableTimesSections]);

    usePageTracking({
        pageName: "SelectActivityTime",
        pageType: PageType.Page,
        metadata: {
            activityNames: availableTimesSections.map((section) => section.name),
            activityIds: availableTimesSections.map((section) => section.id.toString()),
            isEditMode: true,
            timeSlotCount: timeSlotCount,
        },
    });

    return (
        <Stack spacing={ThemeSpaceVariable.Medium}>
            <Heading as="h3" fontSize={ThemeFontSizeVariable.XLarge}>
                {t("editBooking.chooseNewTime")}
            </Heading>
            <Box px={ThemeSpaceVariable.None} className="available-times">
                {isLoading && <TimeSetSkeleton px={ThemeSpaceVariable.None} />}
                {!isLoading &&
                    availableTimesSections.map((section) => (
                        <SectionOfAvailableTimes
                            key={`section${section.id}`}
                            availableTimesSection={section}
                            selectedTime={selectedTimeSlot}
                            onTimeClickOverride={setTime}
                            isEditBookingMode
                        />
                    ))}
            </Box>
        </Stack>
    );
};

export default EditTimeView;
